import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, Navigate, useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { ErrorMessage } from "@/components/ErrorMessage";
import { useStaffAuth } from "@/lib/use-staff-auth";
import { staffHomePath } from "@/lib/staff-capabilities";
import { StaffAuthCard, inputClass, linkClass, primaryButtonClass } from "./StaffAuthCard";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function StaffLoginPage(): React.ReactElement {
  const navigate = useNavigate();
  const { login, restoring, staff } = useStaffAuth();
  const loginMutation = useMutation({
    mutationFn: ({ email, password }: LoginFormData) => login(email, password),
    onSuccess: (staff) => {
      void navigate(staffHomePath(staff.role), { replace: true });
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) });

  const onSubmit = (data: LoginFormData): void => {
    loginMutation.mutate(data);
  };

  if (restoring) {
    return (
      <p role="status" className="p-6 text-sm text-gray-600">
        Signing you in…
      </p>
    );
  }
  if (staff) {
    return <Navigate to={staffHomePath(staff.role)} replace />;
  }

  return (
    <StaffAuthCard title="Staff sign in">
      <ErrorMessage message={loginMutation.error instanceof Error ? loginMutation.error.message : undefined} />

      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="mt-4 space-y-4">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
            Email
          </label>
          <input id="email" type="email" autoComplete="email" {...register("email")} className={inputClass} />
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium text-gray-700">
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            {...register("password")}
            className={inputClass}
          />
          {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
        </div>

        <button type="submit" disabled={loginMutation.isPending} className={primaryButtonClass}>
          {loginMutation.isPending ? "Signing in..." : "Sign in"}
        </button>
      </form>

      <p className="mt-4 text-center text-sm">
        <Link to="/staff/forgot-password" className={linkClass}>
          Forgot password?
        </Link>
      </p>
    </StaffAuthCard>
  );
}
